import React from 'react';

const MAX_RETRY_COUNT = 3;
const RETRY_DELAY = 1000;
const STORAGE_KEY = 'lazy-load-retry-counts';
const RELOAD_STORAGE_KEY = 'lazy-load-needs-reload';

// Storage utilities
const storage = {
  getRetryCounts(): Map<string, number> {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return new Map();
      
      const parsed = JSON.parse(stored);
      return new Map(Object.entries(parsed));
    } catch (e) {
      console.error('Failed to parse retry counts from storage:', e);
      return new Map();
    }
  },

  saveRetryCounts(counts: Map<string, number>) {
    try {
      const obj = Object.fromEntries(counts.entries());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (e) {
      console.error('Failed to save retry counts to storage:', e);
    }
  },

  checkNeedsReload(): boolean {
    const needsReload = localStorage.getItem(RELOAD_STORAGE_KEY) === 'true';
    if (needsReload) {
      localStorage.removeItem(RELOAD_STORAGE_KEY);
    }
    return needsReload;
  },

  setNeedsReload() {
    localStorage.setItem(RELOAD_STORAGE_KEY, 'true');
  },

  clearReloadFlag() {
    localStorage.removeItem(RELOAD_STORAGE_KEY);
  },
};

// Track retry counts per module to prevent infinite loops
const retryCountMap = storage.getRetryCounts();

// Error classification
type ErrorType = 'chunk' | 'mime' | 'network' | 'other';

// Check if error matches chunk loading patterns
function isChunkError(error: any): boolean {
  if (error.name === 'ChunkLoadError') return true;
  if (!error.message) return false;
  
  const chunkPatterns = [
    'Loading chunk',
    'Failed to fetch dynamically imported module',
    'Importing a module script failed'
  ];
  
  return chunkPatterns.some(pattern => error.message.includes(pattern));
}

// Check if error matches MIME type patterns
function isMimeError(message: string): boolean {
  const mimePatterns = ['MIME type', 'text/html', 'application/json'];
  return mimePatterns.some(pattern => message.includes(pattern));
}

// Check if error matches network patterns
function isNetworkError(message: string): boolean {
  const networkPatterns = ['NetworkError', 'Failed to fetch'];
  return networkPatterns.some(pattern => message.includes(pattern));
}

function classifyError(error: any): ErrorType {
  if (!error.message) return 'other';
  
  const message = error.message;
  
  if (isChunkError(error)) return 'chunk';
  if (isMimeError(message)) return 'mime';
  if (isNetworkError(message)) return 'network';
  
  return 'other';
}

// Handle successful import
function handleSuccessfulImport(moduleId: string) {
  if (retryCountMap.has(moduleId)) {
    retryCountMap.delete(moduleId);
    storage.saveRetryCounts(retryCountMap);
  }
  storage.clearReloadFlag();
}

// Handle max retries exceeded
function handleMaxRetriesExceeded(moduleId: string, componentName?: string): never {
  console.error(`Failed to load component after ${MAX_RETRY_COUNT} retries`);
  retryCountMap.delete(moduleId);
  storage.saveRetryCounts(retryCountMap);
  
  const componentInfo = componentName ? ` ${componentName}` : '';
  throw new Error(
    `Failed to load component${componentInfo} after ${MAX_RETRY_COUNT} retries. Please refresh the page.`
  );
}

// Force page reload
function forcePageReload(reason: string): never {
  console.warn(reason);
  window.location.reload();
  throw new Error('Reloading page...');
}

// Handle retryable error
async function handleRetryableError(
  moduleId: string,
  currentRetryCount: number,
  errorType: ErrorType,
  componentName?: string
) {
  const newRetryCount = currentRetryCount + 1;
  retryCountMap.set(moduleId, newRetryCount);
  storage.saveRetryCounts(retryCountMap);
  
  console.warn(
    `${errorType} error detected for ${componentName || 'component'}. ` +
    `Attempt ${newRetryCount}/${MAX_RETRY_COUNT}`
  );
  
  // For persistent MIME type errors, force reload
  if (errorType === 'mime' && newRetryCount >= 2) {
    storage.setNeedsReload();
    forcePageReload('MIME type error persists, reloading page...');
  }
  
  // Wait before retrying
  await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
  
  // Clear module cache if possible (for Vite HMR)
  if (import.meta.hot) {
    import.meta.hot.invalidate();
  }
}

export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  componentName?: string
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    const moduleId = componentName || importFn.toString();
    
    // Check if we should do a full page reload on mount
    if (storage.checkNeedsReload()) {
      forcePageReload('Page reload requested...');
    }
    
    const attemptImport = async (): Promise<{ default: T }> => {
      try {
        const result = await importFn();
        handleSuccessfulImport(moduleId);
        return result;
      } catch (error: any) {
        const currentRetryCount = retryCountMap.get(moduleId) || 0;
        
        // Check if we've exceeded max retries
        if (currentRetryCount >= MAX_RETRY_COUNT) {
          handleMaxRetriesExceeded(moduleId, componentName);
        }
        
        const errorType = classifyError(error);
        
        // Only retry for known error types
        if (errorType === 'chunk' || errorType === 'mime' || errorType === 'network') {
          await handleRetryableError(moduleId, currentRetryCount, errorType, componentName);
          return attemptImport();
        }
        
        // For other errors, throw immediately
        throw error;
      }
    };
    
    return attemptImport();
  });
}


// Initialize by checking if we have persistent reload flag
if (typeof window !== 'undefined' && storage.checkNeedsReload()) {
  forcePageReload('Initial page reload requested...');
}